import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { 
  Eye, 
  EyeOff, 
  Pencil, 
  Trash2, 
  GripVertical,
  GraduationCap,
  BookOpen,
  Users,
  Video,
  Copy,
  BarChart3,
  Layout,
  Bot,
  Mail,
  Flame,
  Share2,
  FileText,
  Settings,
  ListTodo,
  Home,
  Bell,
  MessageSquare,
  ClipboardList,
  CalendarDays,
  LucideIcon,
  Check,
  X,
  Plus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useAdminNavigation } from "@/contexts/AdminNavigationContext";
import {
  applyAdminSidebarVisibility,
  deriveAdminSidebarVisibility,
  loadAdminSidebarVisibility,
  saveAdminSidebarVisibility,
} from "@/lib/adminSidebarVisibility";

interface SidebarItem {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
  isVisible: boolean;
}

interface SidebarSection {
  id: string;
  label: string;
  icon: LucideIcon;
  isVisible: boolean;
  items: SidebarItem[];
}

const REQUIRED_SECTION_ID = "notifications";
const REQUIRED_ITEM_ID = "notifications";

const requiredNotificationsItem: SidebarItem = {
  id: REQUIRED_ITEM_ID,
  label: "Notifications",
  icon: Bell,
  path: "/admin/notifications",
  isVisible: true,
};

const ensureNotificationsVisible = (sections: SidebarSection[]): SidebarSection[] => {
  const existing = sections.find((section) => section.id === REQUIRED_SECTION_ID);
  const items = existing ? [...existing.items] : [];
  const itemIndex = items.findIndex((item) => item.id === REQUIRED_ITEM_ID);

  if (itemIndex === -1) {
    items.unshift({ ...requiredNotificationsItem });
  } else {
    items[itemIndex] = {
      ...items[itemIndex],
      ...requiredNotificationsItem,
      isVisible: true,
    };
  }

  const normalizedSection: SidebarSection = {
    id: REQUIRED_SECTION_ID,
    label: "Notifications",
    icon: Bell,
    isVisible: true,
    items,
  };

  const rest = sections.filter((section) => section.id !== REQUIRED_SECTION_ID);
  return [normalizedSection, ...rest];
};

// Initial sidebar data
const getInitialSections = (): SidebarSection[] => [
  {
    id: "notifications",
    label: "Notifications",
    icon: Bell,
    isVisible: true,
    items: [
      { id: "notifications", label: "Notifications", icon: Bell, path: "/admin/notifications", isVisible: true },
      { id: "messaging", label: "Messaging", icon: MessageSquare, path: "/admin/notifications?tab=messaging", isVisible: true },
    ]
  },
  {
    id: "education",
    label: "Education",
    icon: GraduationCap,
    isVisible: true,
    items: [
      { id: "education", label: "Education", icon: GraduationCap, path: "/admin/education", isVisible: true },
    ]
  },
  {
    id: "creators",
    label: "Creators",
    icon: Users,
    isVisible: true,
    items: [
      { id: "applications", label: "Applications", icon: ClipboardList, path: "/admin/creators?filter=applications", isVisible: true },
      { id: "all-creators", label: "All Creators", icon: Users, path: "/admin/creators?filter=all", isVisible: true },
      { id: "categories", label: "Categories", icon: Users, path: "/admin/creators?filter=categories", isVisible: true },
      { id: "global", label: "Global", icon: Settings, path: "/admin/creators?filter=global", isVisible: true },
    ]
  },
  {
    id: "creating",
    label: "Creating",
    icon: Video,
    isVisible: true,
    items: [
      { id: "video", label: "Video", icon: Video, path: "/admin/video", isVisible: true },
      { id: "duplicating", label: "Duplicate", icon: Copy, path: "/admin/duplicating", isVisible: true },
    ]
  },
  {
    id: "social-media",
    label: "Social media",
    icon: BarChart3,
    isVisible: true,
    items: [
      { id: "tracking", label: "Tracking", icon: BarChart3, path: "/admin/tracking", isVisible: true },
      { id: "compass", label: "Compass", icon: Layout, path: "/admin/compass", isVisible: true },
      { id: "calendar", label: "Calendar", icon: CalendarDays, path: "/admin/calendar", isVisible: true },
      { id: "viral-content", label: "Viral content", icon: Flame, path: "/admin/viral-content", isVisible: true },
    ]
  },
  {
    id: "referrals",
    label: "Referrals",
    icon: Share2,
    isVisible: true,
    items: [
      { id: "referrals", label: "Referrals", icon: Share2, path: "/admin/referrals", isVisible: true },
    ]
  },
  {
    id: "invoices",
    label: "Invoices",
    icon: FileText,
    isVisible: true,
    items: [
      { id: "invoices", label: "Invoices", icon: FileText, path: "/admin/invoices", isVisible: true },
    ]
  },
  {
    id: "settings",
    label: "Settings",
    icon: Settings,
    isVisible: true,
    items: [
      { id: "settings", label: "Settings", icon: Settings, path: "/admin/settings", isVisible: true },
      { id: "analytics", label: "Analytics", icon: BarChart3, path: "/admin/analytics", isVisible: true },
    ]
  },
];

// Sortable Main Section Icon
interface SortableMainIconProps {
  section: SidebarSection;
  isActive: boolean;
  onToggleVisibility: () => void;
  onEdit: () => void;
  onDelete: () => void;
  editMode: boolean;
}

const SortableMainIcon = ({ 
  section, 
  isActive, 
  onToggleVisibility, 
  onEdit, 
  onDelete,
  editMode 
}: SortableMainIconProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const SectionIcon = section.icon;

  if (!editMode) {
    return (
      <div className="px-2 mb-1">
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            <Link
              to={section.items[0]?.path || "#"}
              className={`w-full flex items-center justify-center p-2.5 rounded-lg transition-colors relative ${
                !section.isVisible ? 'opacity-40' : ''
              } ${
                isActive
                  ? "bg-red-500/20 text-red-500"
                  : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
              }`}
            >
              <SectionIcon className="w-5 h-5" />
            </Link>
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-foreground text-background px-3 py-1.5 text-sm font-medium">
            {section.label}
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style} className="px-2 mb-1">
      <div
        className={`flex items-center justify-center gap-1 p-1.5 rounded-lg transition-colors relative ${
          !section.isVisible ? 'opacity-40' : ''
        } ${
          isActive
            ? "bg-red-500/20"
            : "bg-muted/30 hover:bg-muted/50"
        }`}
      >
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1">
          <GripVertical className="w-3 h-3 text-muted-foreground" />
        </div>
        <SectionIcon className={`w-4 h-4 ${isActive ? 'text-red-500' : 'text-muted-foreground'}`} />
        <button 
          onClick={onToggleVisibility}
          className="p-0.5 hover:bg-muted rounded"
        >
          {section.isVisible ? (
            <Eye className="w-3 h-3 text-green-500" />
          ) : (
            <EyeOff className="w-3 h-3 text-muted-foreground" />
          )}
        </button>
      </div>
    </div>
  );
};

// Sortable Sub Item
interface SortableSubItemProps {
  item: SidebarItem;
  sectionId: string;
  isActive: boolean;
  onToggleVisibility: () => void;
  onEdit: () => void;
  onDelete: () => void;
  editMode: boolean;
  isEditing: boolean;
  editValue: string;
  onEditValueChange: (value: string) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
}

const SortableSubItem = ({
  item,
  sectionId,
  isActive,
  onToggleVisibility,
  onEdit,
  onDelete,
  editMode,
  isEditing,
  editValue,
  onEditValueChange,
  onSaveEdit,
  onCancelEdit
}: SortableSubItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `${sectionId}:${item.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1 px-2 py-1.5 bg-muted/50 rounded-lg mb-1">
        <Input
          value={editValue}
          onChange={(e) => onEditValueChange(e.target.value)}
          className="h-7 text-sm flex-1"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSaveEdit();
            if (e.key === 'Escape') onCancelEdit();
          }}
        />
        <Button variant="ghost" size="icon" className="h-6 w-6 min-h-[44px] min-w-[44px]" onClick={onSaveEdit} aria-label="Save">
          <Check className="w-3 h-3 text-green-500" />
        </Button>
        <Button variant="ghost" size="icon" className="h-6 w-6 min-h-[44px] min-w-[44px]" onClick={onCancelEdit} aria-label="Cancel">
          <X className="w-3 h-3 text-destructive" />
        </Button>
      </div>
    );
  }

  if (!editMode) {
    return (
      <Link
        to={item.path}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors mb-1 ${
          !item.isVisible ? 'opacity-40' : ''
        } ${
          isActive
            ? "bg-red-500/20 text-red-500 font-medium"
            : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
        }`}
      >
        <span className="truncate">{item.label}</span>
      </Link>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm transition-colors mb-1 ${
        !item.isVisible ? 'opacity-40' : ''
      } ${
        isActive
          ? "bg-red-500/20"
          : "bg-muted/20 hover:bg-muted/40"
      }`}
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1">
        <GripVertical className="w-3 h-3 text-muted-foreground" />
      </div>
      <span className={`flex-1 truncate text-xs ${isActive ? 'text-red-500 font-medium' : 'text-foreground'}`}>
        {item.label}
      </span>
      <div className="flex items-center gap-0.5">
        <button 
          onClick={onToggleVisibility}
          className="p-1 hover:bg-muted rounded"
        >
          {item.isVisible ? (
            <Eye className="w-3 h-3 text-green-500" />
          ) : (
            <EyeOff className="w-3 h-3 text-muted-foreground" />
          )}
        </button>
        <button 
          onClick={onEdit}
          className="p-1 hover:bg-muted rounded"
        >
          <Pencil className="w-3 h-3 text-muted-foreground hover:text-foreground" />
        </button>
        <button 
          onClick={onDelete}
          className="p-1 hover:bg-destructive/20 rounded"
        >
          <Trash2 className="w-3 h-3 text-destructive" />
        </button>
      </div>
    </div>
  );
};

interface AdminSidebarManagerProps {
  activeSection: string;
  onSectionsChange?: (sections: SidebarSection[]) => void;
  userRole?: string | null;
  allowedTabs?: string[];
}

export const AdminSidebarManager = ({ activeSection, onSectionsChange, userRole, allowedTabs = [] }: AdminSidebarManagerProps) => {
  const location = useLocation();
  const { toast } = useToast();
  const { sharedHeaderDivider } = useAdminNavigation();
  const isAdmin = userRole === "admin";

  const hydrateSections = (base: SidebarSection[]) => {
    const saved = loadAdminSidebarVisibility();
    const hydrated = applyAdminSidebarVisibility(base, saved);
    return ensureNotificationsVisible(hydrated);
  };

  const [sections, setSections] = useState<SidebarSection[]>(() => {
    const initial = getInitialSections();
    // If not admin and has allowed_tabs, filter sections
    if (userRole && userRole !== 'admin' && allowedTabs.length > 0) {
      // Filter out settings section for non-admin users - they should never see Team Management
      const filtered = initial
        .filter(section => section.id !== 'settings') // Hide settings from non-admins
        .map(section => ({
          ...section,
          isVisible: allowedTabs.includes(section.id),
          items: section.items.map(item => ({
            ...item,
            isVisible: allowedTabs.includes(item.id) || allowedTabs.includes(section.id)
          }))
        })).filter(section => allowedTabs.includes(section.id) || section.items.some(i => allowedTabs.includes(i.id)));

      // Apply saved visibility (can only hide more, never show restricted tabs)
      return hydrateSections(filtered);
    }
    return hydrateSections(initial);
  });
  const [editMode, setEditMode] = useState(false);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [addingToSection, setAddingToSection] = useState<string | null>(null);
  const [newItemLabel, setNewItemLabel] = useState("");
  const [newItemPath, setNewItemPath] = useState("");

  // Update sections when userRole or allowedTabs change
  useEffect(() => {
    const initial = getInitialSections();
    if (userRole && userRole !== 'admin' && allowedTabs.length > 0) {
      // Filter out settings section for non-admin users - they should never see Team Management
      const filteredSections = initial
        .filter(section => section.id !== 'settings') // Hide settings from non-admins
        .map(section => ({
          ...section,
          isVisible: allowedTabs.includes(section.id),
          items: section.items.map(item => ({
            ...item,
            isVisible: allowedTabs.includes(item.id) || allowedTabs.includes(section.id)
          }))
        })).filter(section => allowedTabs.includes(section.id) || section.items.some(i => allowedTabs.includes(i.id)));
      setSections(hydrateSections(filteredSections));
    } else if (userRole === 'admin') {
      setSections(hydrateSections(initial));
    }
  }, [userRole, allowedTabs]);

  // Persist visibility changes so the "eye" toggles don't reset on navigation.
  useEffect(() => {
    saveAdminSidebarVisibility(deriveAdminSidebarVisibility(sections));
    onSectionsChange?.(sections);
  }, [sections, onSectionsChange]);

  const displaySections = useMemo(() => {
    if (editMode) return ensureNotificationsVisible(sections);

    // Admin always sees all sections (even hidden ones, but styled differently)
    // Non-admin users only see visible sections
    if (isAdmin) {
      return ensureNotificationsVisible(sections);
    }
    
    // For clients: hidden = actually removed from navigation
    const visibleSections = sections
      .filter((s) => s.isVisible)
      .map((s) => ({ ...s, items: s.items.filter((i) => i.isVisible) }))
      .filter((s) => s.items.length > 0);
    return ensureNotificationsVisible(visibleSections);
  }, [sections, editMode, isAdmin]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const isActiveRoute = (path: string) => {
    const basePath = path.split("?")[0];
    return location.pathname === basePath || location.pathname.startsWith(basePath + "/");
  };

  const isItemActive = (item: SidebarItem) => {
    const itemPath = item.path.split("?")[0];
    const itemQuery = item.path.includes("?") ? item.path.split("?")[1] : "";
    const currentQuery = location.search.replace("?", "");
    return location.pathname === itemPath && 
      (itemQuery === "" ? !currentQuery || currentQuery === "filter=all" : currentQuery === itemQuery);
  };

  // Mapping from admin sidebar section/item IDs to dashboard_tab_settings tab_ids
  const adminToClientTabMapping: Record<string, string> = {
    // Sections
    "education": "education",
    "creating": "creating",
    "social-media": "social-media",
    "referrals": "referrals",
    // Items
    "video": "video",
    "duplicating": "duplicating",
    "viral-content": "viral-content",
    "calendar": "calendar",
    "tracking": "tracking",
    "compass": "compass",
    "invoices": "invoices",
    "knowledge-bot": "knowledge-bot",
  };

  const syncVisibilityToDatabase = async (tabId: string, isVisible: boolean) => {
    const clientTabId = adminToClientTabMapping[tabId];
    if (clientTabId) {
      try {
        const { error } = await supabase
          .from("dashboard_tab_settings")
          .upsert({ 
            tab_id: clientTabId, 
            is_visible: isVisible,
            updated_at: new Date().toISOString()
          }, { onConflict: 'tab_id' });
        
        if (error) {
          console.error("Failed to sync tab visibility:", error);
        }
      } catch (err) {
        console.error("Failed to sync tab visibility:", err);
      }
    }
  };

  const handleToggleSectionVisibility = async (sectionId: string) => {
    if (isAdmin && sectionId === REQUIRED_SECTION_ID) {
      toast({ title: "Notifications is always visible" });
      return;
    }
    const currentSection = sections.find(s => s.id === sectionId);
    const newVisibility = !currentSection?.isVisible;
    
    setSections(prev => prev.map(s => 
      s.id === sectionId ? { ...s, isVisible: newVisibility } : s
    ));
    
    // Sync section visibility to database
    await syncVisibilityToDatabase(sectionId, newVisibility);
    
    toast({ title: "Visibility updated" });
  };

  const handleToggleItemVisibility = async (sectionId: string, itemId: string) => {
    if (isAdmin && sectionId === REQUIRED_SECTION_ID && itemId === REQUIRED_ITEM_ID) {
      toast({ title: "Notifications is always visible" });
      return;
    }
    const currentItem = sections.find(s => s.id === sectionId)?.items.find(i => i.id === itemId);
    const newVisibility = !currentItem?.isVisible;
    
    setSections(prev => prev.map(s => 
      s.id === sectionId 
        ? { ...s, items: s.items.map(i => i.id === itemId ? { ...i, isVisible: newVisibility } : i) }
        : s
    ));
    
    // Sync item visibility to database
    await syncVisibilityToDatabase(itemId, newVisibility);
    
    toast({ title: "Visibility updated" });
  };

  const handleStartEdit = (sectionId: string, itemId: string, currentLabel: string) => {
    setEditingItem(`${sectionId}:${itemId}`);
    setEditValue(currentLabel);
  };

  const handleSaveEdit = (sectionId: string, itemId: string) => {
    setSections(prev => prev.map(s => 
      s.id === sectionId 
        ? { ...s, items: s.items.map(i => i.id === itemId ? { ...i, label: editValue } : i) }
        : s
    ));
    setEditingItem(null);
    setEditValue("");
    toast({ title: "Label updated" });
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditValue("");
  };

  const handleDeleteItem = (sectionId: string, itemId: string) => {
    if (isAdmin && sectionId === REQUIRED_SECTION_ID && itemId === REQUIRED_ITEM_ID) {
      toast({ title: "Notifications is required" });
      return;
    }
    setSections(prev => prev.map(s => 
      s.id === sectionId 
        ? { ...s, items: s.items.filter(i => i.id !== itemId) }
        : s
    ));
    toast({ title: "Item deleted" });
  };

  const handleDeleteSection = (sectionId: string) => {
    if (isAdmin && sectionId === REQUIRED_SECTION_ID) {
      toast({ title: "Notifications is required" });
      return;
    }
    setSections(prev => prev.filter(s => s.id !== sectionId));
    toast({ title: "Section deleted" });
  };

  const handleAddItem = (sectionId: string) => {
    if (!newItemLabel.trim()) {
      toast({ title: "Enter a label", variant: "destructive" });
      return;
    }
    
    const newItem: SidebarItem = {
      id: `custom-${Date.now()}`,
      label: newItemLabel.trim(),
      icon: Home,
      path: newItemPath.trim() || `/admin/${sectionId}/${newItemLabel.toLowerCase().replace(/\s+/g, '-')}`,
      isVisible: true,
    };
    
    setSections(prev => prev.map(s => 
      s.id === sectionId 
        ? { ...s, items: [...s.items, newItem] }
        : s
    ));
    
    setAddingToSection(null);
    setNewItemLabel("");
    setNewItemPath("");
    toast({ title: "Page added" });
  };

  const handleMainDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (active.id === REQUIRED_SECTION_ID || over.id === REQUIRED_SECTION_ID) return;

    setSections(prev => {
      const oldIndex = prev.findIndex(s => s.id === active.id);
      const newIndex = prev.findIndex(s => s.id === over.id);
      const reordered = arrayMove(prev, oldIndex, newIndex);
      return ensureNotificationsVisible(reordered);
    });
  };

  const handleSubDragEnd = (sectionId: string) => (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeId = (active.id as string).split(':')[1];
    const overId = (over.id as string).split(':')[1];

    setSections(prev => prev.map(s => {
      if (s.id !== sectionId) return s;
      const oldIndex = s.items.findIndex(i => i.id === activeId);
      const newIndex = s.items.findIndex(i => i.id === overId);
      return { ...s, items: arrayMove(s.items, oldIndex, newIndex) };
    }));
  };

  // Find active section based on current route
  const currentActiveSectionData = displaySections.find(s => 
    s.items.some(item => isActiveRoute(item.path.split("?")[0]))
  ) || displaySections.find(s => s.id === activeSection) || displaySections[0];

  const getRoleBadge = () => {
    switch (userRole) {
      case 'admin':
        return { label: 'Admin', bgClass: 'bg-red-500/20', textClass: 'text-red-500' };
      case 'editor':
        return { label: 'Editor', bgClass: 'bg-blue-500/20', textClass: 'text-blue-500' };
      case 'viewer':
        return { label: 'Viewer', bgClass: 'bg-gray-500/20', textClass: 'text-gray-500' };
      default:
        return { label: 'User', bgClass: 'bg-gray-500/20', textClass: 'text-gray-500' };
    }
  };

  const roleBadge = getRoleBadge();

  return (
    <>
      {/* Icon Sidebar */}
      <aside className="h-screen w-16 bg-background border-r border-border flex flex-col flex-shrink-0">
        {/* Icon Navigation */}
        <nav className="flex-1 overflow-y-auto pt-16 pb-3 min-h-0">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleMainDragEnd}
          >
            <SortableContext 
               items={displaySections.map(s => s.id)} 
              strategy={verticalListSortingStrategy}
            >
              {displaySections.map((section) => {
                const isActive = section.items.some(item => isActiveRoute(item.path.split("?")[0]));
                
                return (
                  <SortableMainIcon
                    key={section.id}
                    section={section}
                    isActive={isActive}
                    onToggleVisibility={() => handleToggleSectionVisibility(section.id)}
                    onEdit={() => {}}
                    onDelete={() => handleDeleteSection(section.id)}
                    editMode={editMode}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </nav>

        {/* Keep the edit toggle pinned at the bottom of the rail on all admin pages */}
        <div className="mt-auto p-2">
          <Button
            variant={editMode ? "default" : "ghost"}
            size="sm"
            className="w-full h-8 text-xs"
            onClick={() => setEditMode(!editMode)}
            data-testid="admin-sidebar-edit-toggle-bottom"
            disabled={!isAdmin}
          >
            {editMode ? <Check className="w-3 h-3" /> : <Pencil className="w-3 h-3" />}
          </Button>
        </div>
      </aside>

      {/* Sub-items Sidebar - Always visible */}
        <aside className="h-screen w-52 bg-background border-r border-border flex flex-col flex-shrink-0">
        <div className={`h-14 px-4 flex items-center ${sharedHeaderDivider ? "" : "border-b border-border"}`}>
          <h2 className="text-[15px] font-semibold text-foreground flex items-center gap-2">
            {currentActiveSectionData?.label || "Dashboard"}
            <span className={`text-xs ${roleBadge.bgClass} ${roleBadge.textClass} px-1.5 py-0.5 rounded`}>{roleBadge.label}</span>
          </h2>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 min-h-0">
          {currentActiveSectionData && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleSubDragEnd(currentActiveSectionData.id)}
            >
              <SortableContext 
                items={currentActiveSectionData.items.map(i => `${currentActiveSectionData.id}:${i.id}`)} 
                strategy={verticalListSortingStrategy}
              >
                {currentActiveSectionData.items.map((item) => {
                  const isActive = isItemActive(item);
                  const isEditing = editingItem === `${currentActiveSectionData.id}:${item.id}`;
                  
                  return (
                    <SortableSubItem
                      key={item.id}
                      item={item}
                      sectionId={currentActiveSectionData.id}
                      isActive={isActive}
                      onToggleVisibility={() => handleToggleItemVisibility(currentActiveSectionData.id, item.id)}
                      onEdit={() => handleStartEdit(currentActiveSectionData.id, item.id, item.label)}
                      onDelete={() => handleDeleteItem(currentActiveSectionData.id, item.id)}
                      editMode={editMode}
                      isEditing={isEditing}
                      editValue={editValue}
                      onEditValueChange={setEditValue}
                      onSaveEdit={() => handleSaveEdit(currentActiveSectionData.id, item.id)}
                      onCancelEdit={handleCancelEdit}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          )}
          
          {/* Add new page form - Only for admins */}
          {isAdmin && (
            addingToSection === currentActiveSectionData?.id ? (
              <div className="mt-2 p-2 bg-muted/50 rounded-lg space-y-2">
                <Input
                  placeholder="Page label"
                  value={newItemLabel}
                  onChange={(e) => setNewItemLabel(e.target.value)}
                  className="h-8 text-sm"
                  autoFocus
                />
                <Input
                  placeholder="Path (optional)"
                  value={newItemPath}
                  onChange={(e) => setNewItemPath(e.target.value)}
                  className="h-8 text-sm"
                />
                <div className="flex gap-1">
                  <Button 
                    size="sm" 
                    className="flex-1 h-7 text-xs"
                    onClick={() => handleAddItem(currentActiveSectionData.id)}
                  >
                    Add
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => {
                      setAddingToSection(null);
                      setNewItemLabel("");
                      setNewItemPath("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setAddingToSection(currentActiveSectionData?.id || null)}
                className="w-full flex items-center gap-2 px-3 py-2 mt-2 rounded-lg text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors border border-dashed border-border"
              >
                <Plus className="w-4 h-4" />
                <span>Add page</span>
              </button>
            )
          )}
        </nav>
      </aside>
    </>
  );
};
